import { defineStore } from 'pinia'
import { shuffle, cloneDeep } from 'lodash-es'
import {
  destroyQuestion,
  replicateQuestion,
  storeQuestion,
  updateQuestion,
  importQuestions,
} from '@/api/questionApi'

import AnswerModel from '@/models/answer.model'
import questionModel from '@/models/question.model'
import { questionRules } from '@/models/questionRules'

import { useCurrentGameStore } from '../game/currentGame'
import { useSwal } from '@/composables'
import { extractClapError } from '@/helpers/errorHelper'

import { v4 as uuidv4 } from 'uuid'

type State = {
  isUpdateFromServer: boolean
  isNew: boolean
  isLoading: boolean
  isShowErrorMessage: boolean
  isQueSettingOpen: boolean // only for mobile mode
  currentQuestion: Question
  isSaved: boolean
  loading: boolean
  error: any
  showIsSaved: boolean
  tempId: string | null
}
export const useQuestionStore = defineStore('question', {
  state: (): State => {
    return {
      tempId: null,
      isUpdateFromServer: false,
      isNew: true,
      isLoading: false,
      isShowErrorMessage: false,
      isQueSettingOpen: false, // only for mobile mode
      currentQuestion: new questionModel(),
      isSaved: true,
      loading: false,
      error: null,
      showIsSaved: false,
    }
  },
  getters: {
    questionIsSaved: state => state.isSaved,
    questionLoading: state => state.loading,
    questionError: state => state.error,
    questionShowIsSaved: state => state.showIsSaved,
    isClearQuestion(): boolean {
      return (
        !this.currentQuestion.question.que &&
        !this.currentQuestion.question.answers?.find(a => a.ans) &&
        !this.currentQuestion.question.link
      )
    },
    answersLength(): number {
      return this.currentQuestion?.question?.answers?.length || 0
    },
    answers(): Answer[] {
      return this.currentQuestion?.question?.answers || []
    },
    questionType(): TypeQuestion {
      return this.currentQuestion?.type
    },
    questionId(): string | undefined {
      return this.currentQuestion?._id
    },
    question(): Question['question'] {
      return this.currentQuestion?.question
    },
    setting(): Question['setting'] {
      return this.currentQuestion?.setting
    },
    rules(): QuestionRules[TypeQuestion]['rules'] {
      return questionRules[this.questionType].rules
    },
    validators(): QuestionRules[TypeQuestion]['validators'] {
      return questionRules[this.questionType].validators
    },
    fields(): QuestionRules[TypeQuestion]['fields'] {
      return questionRules?.[this.questionType]?.fields
    },
    questionDetails(): Question {
      return this.currentQuestion
    },
    disableEdit(): boolean {
      return this.currentQuestion?.disableEdit || false
    },
  },
  actions: {
    // only for mobile mode
    queSettingToggle() {
      const { isQueListOpen } = storeToRefs(useCurrentGameStore())
      if (isQueListOpen.value) {
        useCurrentGameStore().queListToggle()
      }
      this.isQueSettingOpen = !this.isQueSettingOpen
    },
    // answers actions
    setAnswersByIndex(index: number, value: Answer) {
      if (!this.currentQuestion.question?.answers) {
        this.currentQuestion.question.answers = []
      }
      this.currentQuestion.question.answers[index] = value
    },
    setAnswers(value: Answer[]) {
      this.currentQuestion.question.answers = cloneDeep(value)
    },
    async mixAnswers() {
      const q = this.currentQuestion.question
      if (q?.answers) {
        if (this.questionType === 'matchingPairs') {
          const questions = q.questions || []
          const answers = q.answers || []
          const max = Math.max(questions.length, answers.length)
          const pairs = []
          for (let i = 0; i < max; i++) {
            pairs.push({
              que: questions[i] || { _id: uuidv4(), content: '', media: {} },
              ans: answers[i] || { _id: uuidv4(), ans: '', ansMedia: {} },
            })
          }
          const shuffledPairs = shuffle(pairs)

          this.currentQuestion.question.questions = shuffledPairs.map(
            (p, index) => ({
              ...p.que,
              id: index + 1,
            })
          )
          this.currentQuestion.question.answers = shuffledPairs.map(p => p.ans)
        } else {
          this.currentQuestion.question.answers = shuffle([...q.answers])
        }

        this.isSaved = false
        window.dispatchEvent(new CustomEvent('answers-media-updated'))

        await this.updateQuestion()

        window.dispatchEvent(new CustomEvent('answers-media-updated'))
      }
    },
    addAnswer(ans = '', index?: number) {
      if (!this.currentQuestion.question?.answers) {
        this.currentQuestion.question.answers = []
      }

      if (index) {
        this.currentQuestion.question.answers.splice(
          index + 1,
          0,
          new AnswerModel(ans)
        )
      } else {
        this.currentQuestion.question.answers.push(new AnswerModel(ans))
      }
    },
    resetAnswer() {
      this.currentQuestion.question.answers = [new AnswerModel()]
    },
    removeAns(id: string) {
      if (this.answersLength == 1) {
        this.resetAnswer()
      } else {
        this.currentQuestion.question.answers = this.answers.filter(
          a => a._id !== id
        )
      }
    },
    setAllAnsToCorrect() {
      this.currentQuestion.question.answers =
        this.currentQuestion.question.answers?.map(a => ({
          ...a,
          correct: true,
        }))
    },
    addIndexToAnswers(answers: Answer[]) {
      return answers.map((a, i) => ({ ...a, id: i + 1 }))
    },
    // question actions
    setQuestionByKey<T extends keyof Question['question']>(
      key: T,
      value: Question['question'][T]
    ) {
      this.currentQuestion.question[key] = value
    },
    setCurrentQuestionKey(key: string, value: string) {
      this.currentQuestion = { ...this.currentQuestion, [key]: value }
    },
    setQuestionSettingByKey<T extends keyof Question['setting']>(
      key: T,
      value: Question['setting'][T]
    ) {
      this.currentQuestion.setting[key] = value
    },
    setQuestion(value: Question['question']) {
      this.currentQuestion.question = cloneDeep(value)
    },
    setQuestionSetting(value: Question['setting']) {
      this.currentQuestion.setting = cloneDeep(value)
    },
    setQuestionAndDetails(value: Question) {
      this.currentQuestion = cloneDeep(value)
    },
    // game actions
    setQuestionByIndex(index: number) {
      this.isUpdateFromServer = true
      const question = useCurrentGameStore().getQuestionByIndex(index)
      this.setQuestionAndDetails(question)
    },
    clearData() {
      const question = new questionModel()

      this.currentQuestion = cloneDeep(question)
    },

    // error handling
    setShowErrorMessage(isShow: boolean) {
      this.isShowErrorMessage = isShow
    },

    // fetch from api
    async createQuestion() {
      try {
        this.isNew = true
        this.isUpdateFromServer = true
        const tempId = this.tempId

        const {
          game: { _id, user },
        } = useCurrentGameStore()
        if (!this.fields?.find(field => field === 'answers')) {
          delete this.currentQuestion.question.answers
        }
        if (this.questionType == 'timeline' || this.questionType == 'survey') {
          this.setAllAnsToCorrect()
        }
        this.currentQuestion.game = _id
        this.currentQuestion.user = user
        const que = await storeQuestion(this.currentQuestion)
        this.currentQuestion = que
        this.isLoading = false
        this.isNew = true

        this.tempId = tempId
        return que
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
      }
    },
    async updateQuestion() {
      try {
        const questionBody = cloneDeep(this.currentQuestion)

        // For matchingPairs, we don't map answers with correct/id since it uses custom structure
        if (this.questionType !== 'matchingPairs') {
          questionBody.question.answers = questionBody.question.answers?.map(
            (answer, index) => ({
              ...answer,
              correct: ['timeline', 'survey'].includes(this.questionType)
                ? true
                : answer.correct,
              id: index + 1,
            })
          )
        }

        await updateQuestion(questionBody)
        useCurrentGameStore().setGameQuestion(questionBody)
        this.isLoading = false
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
      }
    },
    async deleteQuestion(id: string, index: number) {
      try {
        const { setGameQuestions } = useCurrentGameStore()
        const { questions } = storeToRefs(useCurrentGameStore())
        await destroyQuestion(id)
        setGameQuestions(questions.value.filter(q => q._id !== id))

        if (!questions.value.length) {
          this.clearData()
          this.isNew = true
          this.tempId = uuidv4()
        } else if (index >= 0 && index < questions.value.length) {
          this.setQuestionByIndex(index)
        } else {
          this.setQuestionByIndex(index - 1)
          return index - 1
        }
        return index
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
      }
    },
    async replicateQuestion(id: string, index: number) {
      try {
        this.isUpdateFromServer = true
        const { createQuestionToGame } = useCurrentGameStore()
        const newQuestion = await replicateQuestion(id)
        this.currentQuestion = cloneDeep(newQuestion)
        createQuestionToGame(cloneDeep(newQuestion), index)
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
      }
    },
    async importQuestions(questions: Question[]) {
      try {
        const gameStore = useCurrentGameStore()
        const gameId = gameStore.game._id
        const data = await importQuestions(questions, gameId)
        // Server already $push-ed ids onto the game; sync local list then
        // persist so the client game document matches.
        gameStore.storeManyQuestions(data)
        await gameStore.updateGame(gameStore.game)
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
        throw e
      }
    },
  },
})
